/**
 * NextJS React component for cross-domain session synchronization
 * Mirrors the Vite app's functionality for seamless integration
 */

"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  createCrossOriginMessaging,
  createCrossDomainStorage,
} from "@/lib/utils/index";
import { getDomainInfo } from "@/lib/utils/domainDetection";
import { sessionUtils } from "@/lib/auth/sessionUtils";

export interface CrossDomainSessionSyncProps {
  enableDebug?: boolean;
  autoSync?: boolean;
  syncInterval?: number;
  onSessionChange?: (session: any) => void;
  onError?: (error: Error) => void;
  children?: React.ReactNode;
}

export interface SessionSyncStatus {
  isConnected: boolean;
  lastSync: number | null;
  conflicts: string[];
  debug: Record<string, any>;
}

/**
 * Cross-domain session synchronization component
 */
export function CrossDomainSessionSync({
  enableDebug = process.env.NODE_ENV === "development",
  autoSync = true,
  syncInterval = 30000,
  onSessionChange,
  onError,
  children,
}: CrossDomainSessionSyncProps) {
  const [status, setStatus] = useState<SessionSyncStatus>({
    isConnected: false,
    lastSync: null,
    conflicts: [],
    debug: {},
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const messagingRef = useRef<ReturnType<
    typeof createCrossOriginMessaging
  > | null>(null);
  const storageRef = useRef<ReturnType<typeof createCrossDomainStorage> | null>(
    null
  );
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize cross-domain messaging and storage
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const domainInfo = getDomainInfo();

      // Only initialize in development or production environments
      if (!domainInfo.isDevelopment && !domainInfo.isProduction) {
        if (enableDebug) {
          console.log(
            "[CrossDomainSessionSync] Skipping initialization - not in supported environment"
          );
        }
        return;
      }

      // Initialize messaging
      messagingRef.current = createCrossOriginMessaging({
        enableDebug,
        enableAutoSync: autoSync,
        syncInterval,
      });

      // Initialize storage
      storageRef.current = createCrossDomainStorage({
        enableDebug,
        storageKey: "SUPABASE_SESSION",
        cookieDomain: domainInfo.cookieDomain,
        enablePostMessage: domainInfo.isDevelopment,
      });

      // Set up message handlers
      messagingRef.current.addMessageHandler(handleCrossDomainMessage);

      // Start listening
      messagingRef.current.startListening();

      // Initial sync
      performInitialSync();

      setIsInitialized(true);

      if (enableDebug) {
        console.log("[CrossDomainSessionSync] Initialized successfully");
      }
    } catch (error) {
      console.error("[CrossDomainSessionSync] Initialization error:", error);
      onError?.(error as Error);
    }

    return () => {
      cleanup();
    };
  }, [enableDebug, autoSync, syncInterval, onError]);

  // Handle cross-domain messages
  const handleCrossDomainMessage = useCallback(
    (message: any) => {
      if (enableDebug) {
        console.log("[CrossDomainSessionSync] Received message:", message);
      }

      try {
        switch (message.action) {
          case "SESSION_LOGIN":
            handleSessionLogin(message);
            break;
          case "SESSION_LOGOUT":
            handleSessionLogout(message);
            break;
          case "SESSION_REFRESH":
            handleSessionRefresh(message);
            break;
          case "SESSION_SYNC":
            handleSessionSync(message);
            break;
        }
      } catch (error) {
        console.error(
          "[CrossDomainSessionSync] Error handling message:",
          error
        );
        onError?.(error as Error);
      }
    },
    [enableDebug, onError]
  );

  // Handle session login from other domains
  const handleSessionLogin = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          // Update local storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent component
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[CrossDomainSessionSync] Session login synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[CrossDomainSessionSync] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session logout from other domains
  const handleSessionLogout = useCallback(
    (message: any) => {
      // Clear local storage
      if (storageRef.current) {
        storageRef.current.clearSession();
      }

      // Update status
      updateStatus();

      // Notify parent component
      onSessionChange?.(null);

      if (enableDebug) {
        console.log(
          "[CrossDomainSessionSync] Session logout synced from",
          message.source
        );
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session refresh from other domains
  const handleSessionRefresh = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          // Update local storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent component
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[CrossDomainSessionSync] Session refresh synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[CrossDomainSessionSync] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session sync request
  const handleSessionSync = useCallback(
    (message: any) => {
      // Send current session to requesting domain
      if (storageRef.current && messagingRef.current) {
        const currentSession = storageRef.current.getSessionData();
        if (currentSession) {
          messagingRef.current.broadcastSessionUpdate(
            "SESSION_LOGIN",
            JSON.stringify(currentSession)
          );
        }
      }

      if (enableDebug) {
        console.log(
          "[CrossDomainSessionSync] Session sync requested from",
          message.source
        );
      }
    },
    [enableDebug]
  );

  // Perform initial sync
  const performInitialSync = useCallback(async () => {
    try {
      // Request session sync from other domains
      if (messagingRef.current) {
        messagingRef.current.requestSessionSync();
      }

      // Update status
      updateStatus();

      if (enableDebug) {
        console.log("[CrossDomainSessionSync] Initial sync completed");
      }
    } catch (error) {
      console.error(
        "[CrossDomainSessionSync] Error during initial sync:",
        error
      );
    }
  }, [enableDebug]);

  // Update status
  const updateStatus = useCallback(() => {
    try {
      const sessionStatus = sessionUtils.getCrossDomainSessionStatus();
      const connectionStatus = messagingRef.current?.getConnectionStatus();

      setStatus({
        isConnected: connectionStatus?.isListening || false,
        lastSync: sessionStatus.lastSync,
        conflicts: sessionStatus.conflicts,
        debug: {
          ...sessionStatus.debug,
          connection: connectionStatus,
        },
      });
    } catch (error) {
      console.error("[CrossDomainSessionSync] Error updating status:", error);
    }
  }, []);

  // Manual sync function
  const manualSync = useCallback(async () => {
    try {
      await sessionUtils.syncSessionAcrossDomains();
      updateStatus();

      if (enableDebug) {
        console.log("[CrossDomainSessionSync] Manual sync completed");
      }
    } catch (error) {
      console.error(
        "[CrossDomainSessionSync] Error during manual sync:",
        error
      );
      onError?.(error as Error);
    }
  }, [enableDebug, onError]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    if (messagingRef.current) {
      messagingRef.current.destroy();
      messagingRef.current = null;
    }

    if (enableDebug) {
      console.log("[CrossDomainSessionSync] Cleaned up");
    }
  }, [enableDebug]);

  // Set up periodic status updates
  useEffect(() => {
    if (!isInitialized || !enableDebug) return;

    const interval = setInterval(() => {
      updateStatus();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [isInitialized, updateStatus, enableDebug]);

  // Expose manual sync function via ref
  const syncRef = useRef({ manualSync });

  // Render debug information in development
  if (enableDebug && process.env.NODE_ENV === "development") {
    return (
      <div className="cross-domain-session-sync">
        {children}
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg text-xs max-w-sm">
          <div className="font-bold mb-2">Cross-Domain Session Sync</div>
          <div>Status: {status.isConnected ? "Connected" : "Disconnected"}</div>
          <div>
            Last Sync:{" "}
            {status.lastSync
              ? new Date(status.lastSync).toLocaleTimeString()
              : "Never"}
          </div>
          {status.conflicts.length > 0 && (
            <div className="text-red-400">
              Conflicts: {status.conflicts.length}
            </div>
          )}
          <button
            onClick={manualSync}
            className="mt-2 px-2 py-1 bg-blue-600 rounded text-xs"
          >
            Manual Sync
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Hook for accessing cross-domain session sync functionality
 */
export function useCrossDomainSessionSync() {
  const [isConnected, setIsConnected] = useState(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<string[]>([]);

  const manualSync = useCallback(async () => {
    try {
      await sessionUtils.syncSessionAcrossDomains();
      const status = sessionUtils.getCrossDomainSessionStatus();
      setLastSync(status.lastSync);
      setConflicts(status.conflicts);
    } catch (error) {
      console.error(
        "[useCrossDomainSessionSync] Error during manual sync:",
        error
      );
    }
  }, []);

  const getStatus = useCallback(() => {
    return sessionUtils.getCrossDomainSessionStatus();
  }, []);

  const getDebugInfo = useCallback(() => {
    return sessionUtils.getDebugInfo();
  }, []);

  return {
    isConnected,
    lastSync,
    conflicts,
    manualSync,
    getStatus,
    getDebugInfo,
  };
}

export default CrossDomainSessionSync;
